/**
 * Cross-tree mirror of Liveblocks canvas nodes for consumers outside
 * `CanvasLiveblocksProvider` (explorer rail, onboarding steps).
 *
 * While a node is being dragged, structural changes still publish but
 * position-only drag frames are skipped to avoid ~60Hz re-renders.
 *
 * `nodesSynced` is true only after Liveblocks finishes loading so the
 * explorer does not flash an empty zero state mid-load.
 */

import type { Node } from "@xyflow/react";
import { useStore } from "@xyflow/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  canvasNodesMirrorNeedsUpdate,
  mergeCanvasNodesMirror,
} from "../utils/canvasNodesMirror";

const EMPTY_NODES: Node[] = [];

type CanvasNodesMirrorState = {
  canvasId: string | undefined;
  nodes: Node[];
  nodesSynced: boolean;
};

const DEFAULT_MIRROR_STATE: CanvasNodesMirrorState = {
  canvasId: undefined,
  nodes: EMPTY_NODES,
  nodesSynced: false,
};

const CanvasNodesMirrorNodesContext = createContext<Node[]>(EMPTY_NODES);
const CanvasNodesMirrorSyncedContext = createContext<boolean>(false);
const CanvasNodesMirrorCanvasIdContext = createContext<string | undefined>(
  undefined,
);

type CanvasNodesMirrorDispatchContextValue = {
  publish: (
    update: Partial<CanvasNodesMirrorState> & { canvasId: string },
  ) => void;
  reset: () => void;
};

const CanvasNodesMirrorDispatchContext =
  createContext<CanvasNodesMirrorDispatchContextValue | null>(null);

export function CanvasNodesMirrorProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, setState] =
    useState<CanvasNodesMirrorState>(DEFAULT_MIRROR_STATE);

  const publish = useCallback(
    (update: Partial<CanvasNodesMirrorState> & { canvasId: string }) => {
      setState((prev) => ({
        canvasId: update.canvasId,
        nodes: update.nodes ?? prev.nodes,
        nodesSynced: update.nodesSynced ?? prev.nodesSynced,
      }));
    },
    [],
  );

  const reset = useCallback(() => {
    setState(DEFAULT_MIRROR_STATE);
  }, []);

  const dispatch = useMemo(() => ({ publish, reset }), [publish, reset]);

  return (
    <CanvasNodesMirrorDispatchContext.Provider value={dispatch}>
      <CanvasNodesMirrorCanvasIdContext.Provider value={state.canvasId}>
        <CanvasNodesMirrorSyncedContext.Provider value={state.nodesSynced}>
          <CanvasNodesMirrorNodesContext.Provider value={state.nodes}>
            {children}
          </CanvasNodesMirrorNodesContext.Provider>
        </CanvasNodesMirrorSyncedContext.Provider>
      </CanvasNodesMirrorCanvasIdContext.Provider>
    </CanvasNodesMirrorDispatchContext.Provider>
  );
}

function useCanvasNodesMirrorDispatch(): CanvasNodesMirrorDispatchContextValue {
  const ctx = useContext(CanvasNodesMirrorDispatchContext);
  if (!ctx) {
    throw new Error(
      "Canvas nodes mirror hooks must be used within CanvasNodesMirrorProvider",
    );
  }
  return ctx;
}

function usePublishedCanvasId(): string | undefined {
  return useContext(CanvasNodesMirrorCanvasIdContext);
}

function guardCanvasId(
  consumerCanvasId: string | undefined,
  publishedCanvasId: string | undefined,
): boolean {
  if (!consumerCanvasId || !publishedCanvasId) return false;
  return consumerCanvasId === publishedCanvasId;
}

export function useCanvasNodesMirror(canvasId?: string): Node[] {
  const nodes = useContext(CanvasNodesMirrorNodesContext);
  const publishedCanvasId = usePublishedCanvasId();
  if (canvasId !== undefined && !guardCanvasId(canvasId, publishedCanvasId)) {
    return EMPTY_NODES;
  }
  return nodes;
}

export function useCanvasNodesMirrorSynced(canvasId?: string): boolean {
  const nodesSynced = useContext(CanvasNodesMirrorSyncedContext);
  const publishedCanvasId = usePublishedCanvasId();
  if (canvasId !== undefined && !guardCanvasId(canvasId, publishedCanvasId)) {
    return false;
  }
  return nodesSynced;
}

export function useCanvasNodesMirrorState(canvasId?: string): {
  nodes: Node[];
  nodesSynced: boolean;
} {
  return {
    nodes: useCanvasNodesMirror(canvasId),
    nodesSynced: useCanvasNodesMirrorSynced(canvasId),
  };
}

export function usePublishCanvasNodesMirror(
  canvasId: string | undefined,
  nodes: Node[],
  /** Liveblocks flow storage has finished its initial load. */
  nodesReady: boolean,
): void {
  const { publish, reset } = useCanvasNodesMirrorDispatch();
  const anyDragging = useStore(
    useCallback((s: { nodes: Node[] }) => s.nodes.some((n) => n.dragging), []),
  );
  const prevNodesRef = useRef<Node[]>(EMPTY_NODES);

  useEffect(() => {
    if (!canvasId) return;

    if (!nodesReady) {
      publish({ canvasId, nodesSynced: false });
      return;
    }

    const prev = prevNodesRef.current;

    if (!anyDragging) {
      prevNodesRef.current = nodes;
      publish({ canvasId, nodes, nodesSynced: true });
      return;
    }

    if (!canvasNodesMirrorNeedsUpdate(nodes, prev)) {
      publish({ canvasId, nodesSynced: true });
      return;
    }

    const merged = mergeCanvasNodesMirror(nodes, prev);
    prevNodesRef.current = merged;
    publish({ canvasId, nodes: merged, nodesSynced: true });
  }, [nodes, canvasId, anyDragging, nodesReady, publish]);

  useEffect(() => {
    return () => {
      prevNodesRef.current = EMPTY_NODES;
      reset();
    };
  }, [reset]);
}
